/**
 * Utility helpers for locating text snippets inside editor content,
 * even when the AI response tweaks whitespace or punctuation.
 */

export interface TextMatch {
  found: boolean;
  startIndex: number;
  endIndex: number;
  matchedText: string;
}

const NOT_FOUND: TextMatch = {
  found: false,
  startIndex: 0,
  endIndex: 0,
  matchedText: '',
};

interface NormalizedTextResult {
  normalized: string;
  map: number[];
}

const escapeRegex = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Normalize text (collapse whitespace + lowercase) and keep a map so we can
 * translate normalized indices back to the original string.
 */
const normalizeTextWithMap = (text: string): NormalizedTextResult => {
  const normalizedChars: string[] = [];
  const map: number[] = [];
  let previousWasSpace = true;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (/\s/.test(char)) {
      if (!previousWasSpace) {
        normalizedChars.push(' ');
        map.push(i);
        previousWasSpace = true;
      }
    } else {
      normalizedChars.push(char.toLowerCase());
      map.push(i);
      previousWasSpace = false;
    }
  }

  // Trim leading/trailing spaces from the normalized output
  let start = 0;
  while (start < normalizedChars.length && normalizedChars[start] === ' ') {
    start++;
  }
  let end = normalizedChars.length;
  while (end > start && normalizedChars[end - 1] === ' ') {
    end--;
  }

  return {
    normalized: normalizedChars.slice(start, end).join(''),
    map: map.slice(start, end),
  };
};

const mapRangeToOriginal = (
  map: number[],
  start: number,
  length: number
): { start: number; end: number } | null => {
  if (!map.length || start < 0 || start >= map.length || length <= 0) {
    return null;
  }

  const endIndex = Math.min(map.length - 1, start + length - 1);
  return {
    start: map[start],
    end: map[endIndex] + 1,
  };
};

/**
 * Attempt to locate the search text within the content, tolerating
 * whitespace, casing, and minor punctuation differences.
 */
export function findTextInContent(content: string, searchText: string): TextMatch {
  if (!content || !searchText) {
    return NOT_FOUND;
  }

  const trimmedSearch = searchText.trim();
  if (!trimmedSearch) {
    return NOT_FOUND;
  }

  // 1) Try strict-ish match but allow whitespace differences via regex
  const flexiblePattern = escapeRegex(trimmedSearch).replace(/\s+/g, '\\s+');
  try {
    const regex = new RegExp(flexiblePattern, 'i');
    const match = regex.exec(content);
    if (match && typeof match.index === 'number') {
      const startIndex = match.index;
      const endIndex = startIndex + match[0].length;
      return {
        found: true,
        startIndex,
        endIndex,
        matchedText: content.substring(startIndex, endIndex),
      };
    }
  } catch (error) {
    console.warn('[findTextInContent] Failed to build regex pattern:', error);
  }

  // 2) Simple case-insensitive substring match
  const contentLower = content.toLowerCase();
  const searchLower = trimmedSearch.toLowerCase();
  const simpleIndex = contentLower.indexOf(searchLower);
  if (simpleIndex !== -1) {
    return {
      found: true,
      startIndex: simpleIndex,
      endIndex: simpleIndex + trimmedSearch.length,
      matchedText: content.substring(simpleIndex, simpleIndex + trimmedSearch.length),
    };
  }

  // 3) Normalized comparison (collapse whitespace + lowercase) w/ index map
  const { normalized: normalizedContent, map: contentMap } = normalizeTextWithMap(content);
  const { normalized: normalizedSearch } = normalizeTextWithMap(trimmedSearch);

  if (!normalizedContent || !normalizedSearch) {
    return NOT_FOUND;
  }

  const normalizedIndex = normalizedContent.indexOf(normalizedSearch);
  if (normalizedIndex !== -1) {
    const mappedRange = mapRangeToOriginal(contentMap, normalizedIndex, normalizedSearch.length);
    if (mappedRange) {
      return {
        found: true,
        startIndex: mappedRange.start,
        endIndex: mappedRange.end,
        matchedText: content.substring(mappedRange.start, mappedRange.end),
      };
    }
  }

  // 4) Fuzzy fallback: ensure at least ~70% of the words appear in order
  const searchWords = normalizedSearch.split(' ').filter(Boolean);
  if (!searchWords.length) {
    return NOT_FOUND;
  }

  const minimumMatches = Math.ceil(searchWords.length * 0.7);
  let bestMatch = NOT_FOUND;

  outer: for (let anchorIdx = 0; anchorIdx < searchWords.length; anchorIdx++) {
    const anchorWord = searchWords[anchorIdx];
    let anchorIndex = contentLower.indexOf(anchorWord);
    while (anchorIndex !== -1) {
      let matchedWords = 1;
      let currentPosition = anchorIndex + anchorWord.length;

      for (let i = anchorIdx + 1; i < searchWords.length; i++) {
        const nextWord = searchWords[i];
        const nextIndex = contentLower.indexOf(nextWord, currentPosition);
        if (nextIndex === -1) {
          break;
        }
        matchedWords++;
        currentPosition = nextIndex + nextWord.length;
      }

      if (matchedWords >= minimumMatches) {
        const candidateMatch: TextMatch = {
          found: true,
          startIndex: anchorIndex,
          endIndex: currentPosition,
          matchedText: content.substring(anchorIndex, currentPosition),
        };

        if (
          !bestMatch.found ||
          candidateMatch.matchedText.length > bestMatch.matchedText.length ||
          candidateMatch.startIndex < bestMatch.startIndex
        ) {
          bestMatch = candidateMatch;
        }
      }

      anchorIndex = contentLower.indexOf(anchorWord, anchorIndex + 1);
    }

    if (bestMatch.found) {
      break outer;
    }
  }

  return bestMatch;
}

export function findAllTextInContent(content: string, searchText: string): TextMatch[] {
  const matches: TextMatch[] = [];
  let offset = 0;

  while (offset < content.length) {
    const result = findTextInContent(content.substring(offset), searchText);
    if (!result.found) {
      break;
    }

    const absoluteMatch = {
      ...result,
      startIndex: result.startIndex + offset,
      endIndex: result.endIndex + offset,
    };
    matches.push(absoluteMatch);
    offset = absoluteMatch.endIndex;
  }

  return matches;
}

export function getBestMatch(matches: TextMatch[], searchText = ''): TextMatch {
  if (!matches.length) {
    return NOT_FOUND;
  }

  if (matches.length === 1) {
    return matches[0];
  }

  const searchLower = searchText.toLowerCase();
  return matches.sort((a, b) => {
    const aExact = searchText ? a.matchedText.toLowerCase() === searchLower : false;
    const bExact = searchText ? b.matchedText.toLowerCase() === searchLower : false;

    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;

    const lengthDiff = b.matchedText.length - a.matchedText.length;
    if (lengthDiff !== 0) return lengthDiff;

    return a.startIndex - b.startIndex;
  })[0];
}
